/**
 * Generic Schema Diff — Feature-agnostic column difference computation.
 *
 * sl-oll9.10: Extracted from elster-schema-diff.ts. All features
 * use this with their own mapSchemaToColumns + extractFieldId.
 *
 * SOLID:
 * - S: Only schema diffing, no UI or network
 * - O: New features = new ColumnDefinition[], no code changes here
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type DbColumnType =
  | 'number'
  | 'checkbox'
  | 'date'
  | 'select'
  | 'rich-text';

/** A column that the target schema expects to exist in the database */
export interface ColumnDefinition {
  /** Logical field ID (e.g. "Kz81" for ELSTER, "title" for content schemas) */
  id: string;
  /** Display name for the database column */
  name: string;
  /** BlockSuite column type */
  dbType: DbColumnType;
  /** Human-readable label (for diff display) */
  label: string;
  /** For select columns: available options */
  enumValues?: Record<string, string>;
}

/** A column that currently exists in the BlockSuite database */
export interface ExistingColumn {
  id: string;
  name: string;
  type: string;
}

/** A field that needs to be added to the database */
export interface DiffAddedField {
  fieldId: string;
  columnName: string;
  dbType: DbColumnType;
  label: string;
  enumValues?: Record<string, string>;
}

/** A field that needs to be removed from the database */
export interface DiffRemovedField {
  fieldId: string;
  columnId: string;
  columnName: string;
}

/** Result of schema diff computation */
export interface SchemaDiff {
  added: DiffAddedField[];
  removed: DiffRemovedField[];
  currentVersion: string;
  targetVersion: string;
  isEmpty: boolean;
}

// ─── Core Diff Function ─────────────────────────────────────────────────────

/**
 * Compute diff between target schema columns and existing database columns.
 *
 * @param targetColumns - Columns the schema expects
 * @param existingColumns - Columns currently in the database
 * @param currentVersion - Current schema version stored on the block
 * @param targetVersion - Target schema version
 * @param extractFieldId - Feature-specific function to extract logical field ID from column name
 */
export function computeSchemaDiff(
  targetColumns: ColumnDefinition[],
  existingColumns: ExistingColumn[],
  currentVersion: string | null,
  targetVersion: string,
  extractFieldId: (columnName: string) => string | null
): SchemaDiff {
  const targetIds = new Set(targetColumns.map(c => c.id));

  // Build map: fieldId → existingColumn
  const existingMap = new Map<string, ExistingColumn>();
  for (const col of existingColumns) {
    const fieldId = extractFieldId(col.name);
    if (fieldId !== null) {
      existingMap.set(fieldId, col);
    }
  }
  const existingIds = new Set(existingMap.keys());

  // Added: in target but not in DB
  const added: DiffAddedField[] = [];
  for (const col of targetColumns) {
    if (!existingIds.has(col.id)) {
      added.push({
        fieldId: col.id,
        columnName: col.name,
        dbType: col.dbType,
        label: col.label,
        enumValues: col.enumValues,
      });
    }
  }

  // Removed: in DB but not in target
  const removed: DiffRemovedField[] = [];
  for (const [fieldId, col] of existingMap) {
    if (!targetIds.has(fieldId)) {
      removed.push({
        fieldId,
        columnId: col.id,
        columnName: col.name,
      });
    }
  }

  return {
    added,
    removed,
    currentVersion: currentVersion ?? 'unknown',
    targetVersion,
    isEmpty: added.length === 0 && removed.length === 0,
  };
}
